﻿const submitBtnSelector: string = '[data-recaptcha]';
const recaptchaTokenSelector: string = '[name*=RecaptchaToken]';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      enterprise?: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

/**
 * Initialize reCAPTCHA bindings for elements matching `submitBtnSelector`.
 * @param recaptchaApiKey reCAPTCHA site key
 */
export function initRecaptchas(recaptchaApiKey: string): void {
  const btns = document.querySelectorAll<HTMLElement>(submitBtnSelector);

  btns.forEach((btn) => {
    if (btn.dataset.recaptchaBound) return;
    btn.dataset.recaptchaBound = "true";

    btn.addEventListener("click", async (event: Event) => {
      event.preventDefault();

      const target = event.currentTarget as HTMLButtonElement | HTMLInputElement | null;
      if (!target) return;

      const form = target.form as HTMLFormElement | null;
      if (!form) return;

      const recaptchaInput = form.querySelector<HTMLInputElement>(recaptchaTokenSelector);
      if (recaptchaInput) {
        const action = (target.dataset.recaptcha as string) || "submit";
        await setRecaptchaToken(recaptchaApiKey, form, action, recaptchaInput, target);
      }
    });
  });
}

/**
 * Request reCAPTCHA v3 / Enterprise score token and submit the form when obtained.
 * v2/invisible (widget id) flows are intentionally removed — only site-key (v3) style is supported.
 */
async function setRecaptchaToken(
  recaptchaApiKey: string,
  form: HTMLFormElement,
  action: string,
  element: HTMLInputElement,
  btn: HTMLButtonElement | HTMLInputElement | HTMLElement
): Promise<void> {
  try {
    (btn as HTMLButtonElement | HTMLInputElement).disabled = true;
  } catch {
    // ignore if element doesn't have disabled
  }

  if (!window.grecaptcha) {
    console.error("reCAPTCHA not loaded (window.grecaptcha is undefined)");
    try {
      (btn as HTMLButtonElement | HTMLInputElement).disabled = false;
    } catch {
      // ignore
    }

    return;
  }

  // Prefer enterprise API when available, otherwise use regular grecaptcha
  const api: any = (window.grecaptcha.enterprise ?? window.grecaptcha);

  api.ready(() => {
    const callExecute = (): Promise<string> => {
      return api.execute(String(recaptchaApiKey), { action: action });
    };

    Promise.resolve(callExecute())
      .then((token: string) => {
        if (!token) {
          throw new Error("reCAPTCHA did not return a token");
        }

        element.value = token;
        form.submit();
      })
      .catch((err: unknown) => {
        console.error(err);
        try {
          (btn as HTMLButtonElement | HTMLInputElement).disabled = false;
        } catch {
          // ignore
        }
      });
  });
}
